import { useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Link as LinkIcon, Loader2, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import { uploadRider, importRiderFromUrl, getUploadQuota } from "@/api/client";
import { mapLimit } from "@/lib/concurrency";
import {
  QUOTA_GATE_TOAST,
  QUOTA_TOAST,
  isFreeQuotaExhausted,
  isQuotaError,
  uploadConcurrency,
} from "@/lib/uploadQuota";
import { ONBOARDING_PROGRESS_QUERY_KEY } from "@/hooks/useOnboardingProgress";
import { useAuthStore } from "@/stores/auth";

const ACCEPTED_TYPES = "application/pdf";

function errorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : String(reason ?? "");
}

/** Free-tier gate line under the dropzone once the month's allowance is used up. */
function QuotaGateNote({ limit }: { limit: number }) {
  return (
    <p role="status" className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-ink-3">
      <Lock className="mt-0.5 h-3 w-3 shrink-0 text-warn" strokeWidth={1.6} />
      <span>
        You've used your {limit} free riders this month — they renew on the 1st.{" "}
        <Link to="/settings" className="font-semibold text-accent hover:underline">
          Upgrade to Explorer
        </Link>{" "}
        to keep uploading.
      </span>
    </p>
  );
}

/**
 * Dashboard hero upload card — fast entry point.
 *
 * Drop a PDF (or paste a Drive/Dropbox URL) and the file is uploaded
 * immediately, then we redirect to /riders/upload where the full
 * processing flow handles polling + status. This component does NOT
 * replicate the polling logic — it stays compact and focused on the
 * "drop something fast" intent.
 */
export function DashboardHeroUpload({ compact = false }: { compact?: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const user = useAuthStore((s) => s.user);
  const isAdmin = !!user?.is_admin;

  // Same quota query, gate and error matching as /riders/upload
  // (lib/uploadQuota.ts), so an exhausted Free user gets the upgrade path
  // here too instead of a generic "failed to upload" (v1.77.0).
  const { data: quota } = useQuery({
    queryKey: ["upload-quota"],
    queryFn: getUploadQuota,
  });
  const quotaExhausted = isFreeQuotaExhausted(quota, isAdmin);
  const disabled = busy || quotaExhausted;

  /** Toasts the Free-tier gate when the month's allowance is used up. */
  const gated = useCallback((): boolean => {
    if (!quotaExhausted) return false;
    toast.error(QUOTA_GATE_TOAST, { id: "upload-quota-gate" });
    return true;
  }, [quotaExhausted]);

  const openPicker = () => {
    if (busy || gated()) return;
    fileInputRef.current?.click();
  };

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      if (gated()) return;
      const list = Array.from(files).filter(
        (f) => f.type === "application/pdf"
      );
      if (list.length === 0) {
        toast.error("Drop a PDF file");
        return;
      }
      setBusy(true);
      try {
        // Bound concurrency: at most 3 uploads in flight (don't flood the
        // API); one at a time on Free, whose cap is hard and unlocked server-side.
        const results = await mapLimit(list, uploadConcurrency(quota, isAdmin), (file) =>
          uploadRider(file),
        );
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const quotaHits = results.filter(
          (r) => r.status === "rejected" && isQuotaError(errorMessage(r.reason)),
        ).length;
        const fail = results.length - ok - quotaHits;
        if (ok > 0) {
          toast.success(
            ok === 1 ? "Rider uploaded — extracting…" : `${ok} riders uploaded — extracting…`
          );
          queryClient.invalidateQueries({ queryKey: ["riders"] });
          queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
          queryClient.invalidateQueries({ queryKey: ONBOARDING_PROGRESS_QUERY_KEY });
          navigate("/riders/upload");
        }
        if (quotaHits > 0) {
          // One toast per batch even when several uploads hit the wall.
          toast.error(QUOTA_TOAST, { id: "upload-quota-exceeded" });
        }
        if (fail > 0) {
          toast.error(
            fail === 1
              ? "1 file failed to upload"
              : `${fail} files failed to upload`
          );
        }
      } finally {
        setBusy(false);
        queryClient.invalidateQueries({ queryKey: ["upload-quota"] });
      }
    },
    [gated, isAdmin, navigate, queryClient, quota]
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = "";
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const onImportUrl = async () => {
    const url = importUrl.trim();
    if (!url) return;
    if (gated()) return;
    setBusy(true);
    try {
      await importRiderFromUrl(url);
      toast.success("Rider imported — extracting…");
      setImportUrl("");
      queryClient.invalidateQueries({ queryKey: ["riders"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      queryClient.invalidateQueries({ queryKey: ONBOARDING_PROGRESS_QUERY_KEY });
      navigate("/riders/upload");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Import failed";
      toast.error(isQuotaError(msg) ? QUOTA_TOAST : msg);
    } finally {
      setBusy(false);
      queryClient.invalidateQueries({ queryKey: ["upload-quota"] });
    }
  };

  // Compact mode — the small dropzone card used by the "New" dashboard layout
  // (DS tokens, no URL import row; that stays on the upload page + classic hero).
  if (compact) {
    return (
      <div>
        <div
          onDragEnter={(e) => { e.preventDefault(); if (!quotaExhausted) setDragOver(true); }}
          onDragOver={(e) => { e.preventDefault(); if (!quotaExhausted) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={openPicker}
          role="button"
          tabIndex={0}
          aria-disabled={quotaExhausted || undefined}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPicker();
            }
          }}
          className={`flex flex-col items-center justify-center rounded-ds border border-dashed px-4 py-7 text-center transition-colors ${
            dragOver ? "border-accent bg-accent-soft" : "border-line-3"
          } ${busy ? "pointer-events-none opacity-60" : ""} ${
            quotaExhausted ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-accent"
          }`}
        >
          <div
            className={`mb-2.5 flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
              dragOver ? "bg-accent-soft" : "bg-panel-3"
            }`}
          >
            {busy ? (
              <Loader2 className="h-4.5 w-4.5 animate-spin text-accent" />
            ) : quotaExhausted ? (
              <Lock className="h-4.5 w-4.5 text-ink-3" strokeWidth={1.7} />
            ) : (
              <Upload className={`h-4.5 w-4.5 ${dragOver ? "text-accent" : "text-ink-3"}`} strokeWidth={1.7} />
            )}
          </div>
          <p className="text-sm font-semibold text-ink">
            {busy ? "Uploading…" : quotaExhausted ? "Monthly limit reached" : "Drop rider PDFs here"}
          </p>
          <p className="mt-0.5 text-xs text-ink-4">
            {quotaExhausted
              ? "Your free riders renew on the 1st"
              : "or click to browse · Trace extracts in seconds"}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            disabled={disabled}
            className="hidden"
            onChange={onInputChange}
          />
        </div>
        {quotaExhausted && quota?.limit != null && <QuotaGateNote limit={quota.limit} />}
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 shadow-lg transition-all ${
        dragOver
          ? "border-cyan-400 shadow-cyan-500/20 scale-[1.01]"
          : "border-zinc-800 hover:border-zinc-700"
      }`}
    >
      {/* Decorative cyan glow on drag-over */}
      {dragOver && (
        <div
          className="pointer-events-none absolute inset-0 opacity-50"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, rgba(6,182,212,0.15), transparent 60%)",
          }}
        />
      )}

      <div className="relative p-5 sm:p-6">
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={openPicker}
          role="button"
          tabIndex={0}
          aria-disabled={quotaExhausted || undefined}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPicker();
            }
          }}
          className={`group relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed py-10 px-6 text-center transition-all ${
            dragOver
              ? "border-cyan-400 bg-cyan-500/5"
              : "border-zinc-700 hover:border-cyan-500/50 hover:bg-zinc-900/50"
          } ${busy ? "pointer-events-none opacity-60" : ""} ${
            quotaExhausted ? "cursor-not-allowed opacity-60" : "cursor-pointer"
          }`}
        >
          <div
            className={`mb-3 flex h-14 w-14 items-center justify-center rounded-full transition-all ${
              dragOver
                ? "bg-cyan-500/20 ring-2 ring-cyan-400"
                : "bg-zinc-800/80 group-hover:bg-cyan-500/10 group-hover:ring-2 group-hover:ring-cyan-500/30"
            }`}
          >
            {busy ? (
              <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
            ) : (
              <Upload
                className={`h-6 w-6 transition-colors ${
                  dragOver
                    ? "text-cyan-400"
                    : "text-zinc-400 group-hover:text-cyan-400"
                }`}
              />
            )}
          </div>
          <h2
            className={`text-lg font-semibold tracking-tight transition-colors ${
              dragOver ? "text-cyan-300" : "text-zinc-100"
            }`}
          >
            {busy
              ? "Uploading…"
              : quotaExhausted
                ? "Monthly limit reached"
                : dragOver
                  ? "Drop to upload"
                  : "Drop a rider PDF"}
          </h2>
          <p className="mt-1 text-sm text-zinc-500">
            {busy
              ? "Hang tight"
              : quotaExhausted
                ? "Your free riders renew on the 1st"
                : (
                <>
                  or <span className="text-cyan-400 underline-offset-2 group-hover:underline">click to browse</span>
                </>
              )}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            disabled={disabled}
            className="hidden"
            onChange={onInputChange}
          />
        </div>

        {quotaExhausted && quota?.limit != null && <QuotaGateNote limit={quota.limit} />}

        {/* URL import — small, secondary action */}
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <LinkIcon className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
            <input
              type="url"
              value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && importUrl.trim() && !disabled) {
                  onImportUrl();
                }
              }}
              placeholder="…or paste a Google Drive / Dropbox link"
              disabled={disabled}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-950/60 py-2 pl-9 pr-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-cyan-500/50 focus:outline-none disabled:opacity-50"
            />
          </div>
          <button
            onClick={onImportUrl}
            disabled={!importUrl.trim() || disabled}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-cyan-500 px-4 py-2 text-sm font-semibold text-zinc-950 transition-colors hover:bg-cyan-400 disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-500"
          >
            <FileText className="h-3.5 w-3.5" /> Import
          </button>
        </div>
      </div>
    </div>
  );
}
